"use client";

import Image from "next/image";
import { Button } from "@/components/ui/button";
import { useRef } from "react";
import { useScroll, useTransform, motion } from "motion/react";

interface ArtOfDetailSectionProps {
  eyebrow: string | null;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  imageUrl: string | null;
  cta: string | null;
}

export function ArtOfDetailSection({
  eyebrow = "The Philosophy",
  title = "Architectural",
  subtitle = "Artistry",
  description = "",
  imageUrl = "",
  cta = "Discover More",
}: ArtOfDetailSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  // Adjusted offsets to ensure animation starts immediately and completes smoothly
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "center center"],
  });

  // Transform height and width to create the "expanding window" effect
  // Removed opacity fade-in to ensure it's visible immediately
  const borderRadius = useTransform(scrollYProgress, [0, 1], ["100px", "0px"]);
  const width = useTransform(scrollYProgress, [0, 1], ["60%", "100%"]);
  const height = useTransform(scrollYProgress, [0, 1], ["60vh", "100vh"]);

  // subtle scale to add depth, but ensuring it hits 1
  const scale = useTransform(scrollYProgress, [0, 1], [0.8, 1]);

  return (
    <section
      ref={sectionRef}
      className="relative w-full h-[200vh] bg-transparent"
    >
      <div className="sticky top-0 w-full h-screen flex items-center justify-center overflow-hidden">
        <motion.div
          style={{
            borderRadius,
            width,
            height,
            scale,
          }}
          className="relative bg-background overflow-hidden flex items-center z-10 shadow-2xl origin-center"
        >
          <div className="w-full h-full flex flex-col md:flex-row items-center">
            {/* Text Content */}
            <div className="w-full md:w-1/2 h-full flex items-center justify-center px-6 md:px-12 py-12 md:py-24 bg-background">
              <div className="space-y-8 max-w-xl">
                <div className="space-y-4">
                  <span className="text-xs font-bold tracking-[0.3em] text-secondary uppercase block">
                    {eyebrow}
                  </span>
                  <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[0.9] text-primary">
                    {title} <br /> <span className="italic text-primary/50">{subtitle}</span>
                  </h2>
                </div>

                <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed">
                  {description}
                </p>

                <Button
                  variant="outline"
                  className="rounded-xl px-8 h-10 uppercase tracking-widest text-xs font-bold border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300"
                >
                  {cta}
                </Button>
              </div>
            </div>

            {/* Image Content */}
            <div className="w-full md:w-1/2 h-full relative p-24">
              <div className="relative w-full h-full overflow-hidden rounded-xl shadow-2xl">
                <Image
                  src={imageUrl || ""}
                  alt="Art of Detail"
                  fill
                  className="object-cover object-center rounded-xl"
                  priority
                />
              </div>
              {/* Subtle gradient to blend image with white background if needed */}
              <div className="absolute inset-y-24 left-24 w-24 bg-gradient-to-r from-white to-transparent opacity-50 pointer-events-none" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
